import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePricingNav } from "../../hooks/usePricingNav";
import { useAuth } from "../../contexts/AuthContext";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Skeleton,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Chip,
  Alert,
} from "@mui/material";
import { LockOutlined, PlayCircleOutlined, Search } from "@mui/icons-material";
import {
  MEMBERSHIP_COLORS,
  ContentContainer,
} from "../../components/membership/MembershipStyles";
import VideoRow from "../../components/membership/VideoRow";
import { videosService, Video } from "../../services/videosService";

const formatDuration = (seconds?: number): string => {
  if (!seconds) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

type RowVideo = {
  id: string;
  title: string;
  duration: string;
  type: string;
  thumbnail: string;
  isLocked?: boolean;
};

const toRowVideo = (video: Video, isLocked: boolean): RowVideo => ({
  id: video._id,
  title: video.title,
  duration: formatDuration(video.duration),
  type: video.category || video.tags?.[0] || "",
  thumbnail:
    video.thumbnailUrl ||
    "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=900&q=80",
  isLocked,
});

// Group videos by whatever categories actually exist in the data, in the
// order they first appear — no hard-coded category list.
const buildCategoryRows = (videos: RowVideo[]) => {
  const byCategory: Record<string, RowVideo[]> = {};
  const order: string[] = [];
  videos.forEach((v) => {
    const key = v.type || "Videos";
    if (!byCategory[key]) {
      byCategory[key] = [];
      order.push(key);
    }
    byCategory[key].push(v);
  });

  return order.map((cat) => ({
    title: cat,
    videos: byCategory[cat],
  }));
};

const Foundation: React.FC = () => {
  const navigate = useNavigate();
  const goToPricing = usePricingNav();
  const { user } = useAuth();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [lockDialogOpen, setLockDialogOpen] = useState(false);

  const isAdmin = user?.type === "admin";
  const isLocked = !isAdmin && !user?.step1Completed;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await videosService.getVideos({ level: "pre-recorded", limit: 100 });
        setVideos(res.videos || []);
      } catch {
        setError("Failed to load Foundation videos.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const categoryRows = React.useMemo(() => {
    const list = videos
      .filter((v) => v.title.toLowerCase().includes(search.toLowerCase()))
      .map((v) => toRowVideo(v, isLocked && v.isAccessible === false));
    return buildCategoryRows(list);
  }, [videos, search, isLocked]);

  return (
    <ContentContainer maxWidth="xl">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: MEMBERSHIP_COLORS.text,
            letterSpacing: "-0.03em",
            fontSize: { xs: "1.8rem", md: "2.4rem" },
            mb: 0.5,
          }}
        >
          Foundation
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: MEMBERSHIP_COLORS.secondary, fontSize: "1.05rem", mb: 3 }}
        >
          Foundational pet-care lessons from Dr.Paw — the essentials every pet parent should know.
        </Typography>

        <TextField
          placeholder="Search videos..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: MEMBERSHIP_COLORS.secondary, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: 320 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "#FFFFFF",
            },
          }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Skeleton while loading */}
      {loading && (
        <Stack spacing={4}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Box key={i}>
              <Skeleton width={120} height={28} sx={{ mb: 1.5 }} />
              <Stack direction="row" spacing={2} sx={{ overflow: "hidden" }}>
                {Array.from({ length: 4 }).map((_, j) => (
                  <Box key={j} sx={{ minWidth: 200, flexShrink: 0 }}>
                    <Skeleton variant="rounded" height={140} sx={{ borderRadius: "12px", mb: 1 }} />
                    <Skeleton width="80%" height={18} />
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {/* Empty state */}
      {!loading && !error && categoryRows.length === 0 && (
        <Box sx={{ py: 10, textAlign: "center" }}>
          <PlayCircleOutlined sx={{ fontSize: 56, color: MEMBERSHIP_COLORS.accent, mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" sx={{ color: MEMBERSHIP_COLORS.text, fontWeight: 700, mb: 1 }}>
            {search ? "No videos match your search" : "No Foundation videos yet"}
          </Typography>
          <Typography variant="body2" sx={{ color: MEMBERSHIP_COLORS.secondary }}>
            {search ? "Try a different search term." : "Check back soon for new content."}
          </Typography>
        </Box>
      )}

      {/* Category rows */}
      {!loading && categoryRows.length > 0 && (
        <>
          <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={`${videos.filter((v) => v.title.toLowerCase().includes(search.toLowerCase())).length} video${videos.length !== 1 ? "s" : ""}`}
              size="small"
              sx={{ bgcolor: "rgba(216,154,122,0.15)", color: MEMBERSHIP_COLORS.accent, fontWeight: 700 }}
            />
          </Box>
          {categoryRows.map((row) => (
            <VideoRow
              key={row.title}
              title={row.title}
              videos={row.videos}
            />
          ))}
        </>
      )}

      {/* Step 1 required dialog */}
      <Dialog
        open={lockDialogOpen}
        onClose={() => setLockDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: "20px", p: 1, maxWidth: 360, width: "100%" } }}
      >
        <DialogContent sx={{ textAlign: "center", py: 4, px: 3 }}>
          <LockOutlined sx={{ fontSize: 44, color: MEMBERSHIP_COLORS.accent, mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: MEMBERSHIP_COLORS.text, mb: 1 }}>
            Step 1 Required
          </Typography>
          <Typography variant="body2" sx={{ color: MEMBERSHIP_COLORS.secondary, mb: 3 }}>
            Complete Step 1 of your membership to unlock Foundation videos.
          </Typography>
          <Stack spacing={1.5}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => { setLockDialogOpen(false); goToPricing(); }}
              sx={{
                backgroundColor: MEMBERSHIP_COLORS.accent,
                color: "#fff",
                borderRadius: "24px",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { backgroundColor: MEMBERSHIP_COLORS.primary },
              }}
            >
              Go to Step 1
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => setLockDialogOpen(false)}
              sx={{ color: MEMBERSHIP_COLORS.secondary, textTransform: "none", fontWeight: 600 }}
            >
              Maybe later
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </ContentContainer>
  );
};

export default Foundation;
